import { useState, useLayoutEffect, useCallback, useRef } from 'react';

/**
 * Interface describing the current viewport state and keyboard visibility
 */
export interface ViewportDetails {
  /** Current visual viewport height in pixels */
  height: number;
  /** Calculated keyboard height in pixels (0 if keyboard is closed) */
  keyboardHeight: number;
  /** Whether the virtual keyboard is currently visible */
  isKeyboardVisible: boolean;
}

/**
 * Calculates the current visual viewport details including keyboard height
 * 
 * This function compares window.innerHeight with window.visualViewport.height
 * to determine if the virtual keyboard is open and calculate its height.
 * 
 * @returns ViewportDetails object with current viewport state
 */
export function getViewportDetails(): ViewportDetails {
  // SSR safety check
  if (typeof window === 'undefined') {
    return {
      height: 0,
      keyboardHeight: 0,
      isKeyboardVisible: false,
    };
  }

  const windowHeight = window.innerHeight;
  const viewportHeight = window.visualViewport?.height ?? windowHeight;
  
  // Calculate keyboard height as the difference between window and viewport height
  const keyboardHeight = Math.max(0, windowHeight - viewportHeight);
  
  // Consider keyboard visible if height difference is significant (> 50px)
  // This threshold helps avoid false positives from minor viewport changes
  const isKeyboardVisible = keyboardHeight > 50;

  return {
    height: viewportHeight,
    keyboardHeight,
    isKeyboardVisible,
  };
}

/**
 * Custom hook that tracks iOS Safari virtual keyboard height and visibility
 * 
 * This hook addresses Safari's unreliable visualViewport resize events by implementing
 * a hybrid approach that combines event listeners with polling during input focus.
 * 
 * ## Safari Bug Workaround
 * 
 * Safari on iOS has inconsistent behavior with visualViewport resize events:
 * - Events sometimes don't fire when keyboard appears/disappears
 * - Timing of events can be delayed or out of sync with actual viewport changes
 * - Different behavior across iOS versions and Safari updates
 * 
 * ## Solution Strategy
 * 
 * 1. **Primary**: Listen to visualViewport resize events (works most of the time)
 * 2. **Fallback**: Use polling during input focus periods (catches missed events)
 * 3. **Optimization**: Only poll when input elements are focused to minimize performance impact
 * 
 * ## Usage
 * 
 * ```typescript
 * const { height, keyboardHeight, isKeyboardVisible } = useVisualViewport();
 * 
 * // Apply keyboard-aware styling
 * const chatStyle = {
 *   paddingBottom: isKeyboardVisible ? `${keyboardHeight}px` : '0px'
 * };
 * ```
 * 
 * @returns ViewportDetails object that updates when viewport changes
 */
export function useVisualViewport(): ViewportDetails {
  const [viewportDetails, setViewportDetails] = useState<ViewportDetails>(() => 
    getViewportDetails()
  );
  
  // Refs to track polling state and prevent memory leaks
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastKnownDetailsRef = useRef<ViewportDetails>(viewportDetails);
  
  /**
   * Updates viewport details and triggers re-render if values changed
   */
  const updateViewportDetails = useCallback(() => {
    const newDetails = getViewportDetails();
    const lastDetails = lastKnownDetailsRef.current;
    
    // Only update state if values actually changed to prevent unnecessary re-renders
    if (
      newDetails.height !== lastDetails.height ||
      newDetails.keyboardHeight !== lastDetails.keyboardHeight ||
      newDetails.isKeyboardVisible !== lastDetails.isKeyboardVisible
    ) {
      lastKnownDetailsRef.current = newDetails;
      setViewportDetails(newDetails);
    }
  }, []);

  /**
   * Starts polling for viewport changes during input focus
   * This catches cases where visualViewport events don't fire reliably
   */
  const startPolling = useCallback(() => {
    // Don't start multiple polling intervals
    if (pollingIntervalRef.current) return;
    
    pollingIntervalRef.current = setInterval(() => {
      updateViewportDetails();
    }, 100); // 100ms polling rate balances responsiveness with performance
  }, [updateViewportDetails]);

  /**
   * Stops polling when input loses focus
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  /**
   * Handles input focus events to start polling workaround
   */
  const handleInputFocus = useCallback(() => {
    startPolling();
  }, [startPolling]);

  /**
   * Handles input blur events to stop polling workaround
   */
  const handleInputBlur = useCallback(() => {
    stopPolling();
    // Do a final update when input loses focus
    updateViewportDetails();
  }, [stopPolling, updateViewportDetails]);

  // Use useLayoutEffect for synchronous DOM updates
  // Falls back to useEffect for SSR compatibility
  const useIsomorphicLayoutEffect = 
    typeof window !== 'undefined' ? useLayoutEffect : () => {};

  useIsomorphicLayoutEffect(() => {
    // Skip if not in browser environment
    if (typeof window === 'undefined') return;

    // Skip if visualViewport is not supported (older browsers)
    if (!window.visualViewport) {
      console.warn('visualViewport API not supported, keyboard detection may be limited');
      return;
    }

    // Set up visualViewport resize listener (primary detection method)
    const handleViewportResize = () => {
      updateViewportDetails();
    };

    // Add visualViewport event listener
    window.visualViewport.addEventListener('resize', handleViewportResize);

    // Set up input focus/blur listeners for polling workaround
    // Target all input elements that can trigger virtual keyboard
    const inputSelectors = 'input, textarea, [contenteditable="true"]';
    const inputElements = document.querySelectorAll(inputSelectors);

    // Add focus/blur listeners to existing input elements
    inputElements.forEach((element) => {
      element.addEventListener('focus', handleInputFocus);
      element.addEventListener('blur', handleInputBlur);
    });

    // Use event delegation for dynamically added input elements
    document.addEventListener('focusin', (event) => {
      const target = event.target as Element;
      if (target.matches && target.matches(inputSelectors)) {
        handleInputFocus();
      }
    });

    document.addEventListener('focusout', (event) => {
      const target = event.target as Element;
      if (target.matches && target.matches(inputSelectors)) {
        handleInputBlur();
      }
    });

    // Initial viewport details update
    updateViewportDetails();

    // Cleanup function
    return () => {
      // Remove visualViewport listener
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }

      // Remove input element listeners
      inputElements.forEach((element) => {
        element.removeEventListener('focus', handleInputFocus);
        element.removeEventListener('blur', handleInputBlur);
      });

      // Remove document event delegation listeners
      document.removeEventListener('focusin', handleInputFocus);
      document.removeEventListener('focusout', handleInputBlur);

      // Clear any active polling interval
      stopPolling();
    };
  }, [updateViewportDetails, handleInputFocus, handleInputBlur, stopPolling]);

  return viewportDetails;
}

/**
 * Utility function to check if the current device is iOS
 * Useful for conditionally applying iOS-specific behavior
 */
export function isIOSDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Utility function to check if visual viewport API is supported
 * Useful for feature detection and graceful degradation
 */
export function isVisualViewportSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return 'visualViewport' in window && window.visualViewport !== null;
}